/**
 * Auditoría · listados PAGINADOS server-side + descargas CSV (decisión 29).
 * Cada función espera filtros opcionales · el backend ignora los
 * undefined y aplica los demás.
 */
import { axiosClient } from "@/shared/api/axios-client";
import type { Paginado } from "@/lib/types";

export interface DocumentoAuditEntry {
  id: string;
  documentoId: string;
  solicitudId: string;
  numeroSolicitud: number;
  categoria: string;
  accion: string;
  nombreOriginal: string;
  extension: string;
  tamanoBytes: number;
  usuarioId: string;
  ocurridoAt: string;
  motivoEliminacion: string | null;
  /** Hotspot 100 · nombre del usuario resuelto en el servidor (null si ya no existe). */
  usuarioNombre?: string | null;
}

export interface AdminAuditEntry {
  id: string;
  ocurridoAt: string;
  categoria: string;
  accion: string;
  entidadId: string;
  entidadDescripcion: string;
  usuarioId: string;
  usuarioEmail: string | null;
  detallesJson: string;
}

/** Filtros del export CSV de cambios (el endpoint /csv conserva categoria+top). */
export interface AuditoriaFiltros {
  categoria?: string;
  top?: number;
}

/** Filtros del export CSV de documentos (el endpoint /csv conserva solicitudId+top). */
export interface AuditoriaDocumentosFiltros {
  solicitudId?: string;
  top?: number;
}

/** Filtros del listado PAGINADO de cambios (GET /auditoria-cambios/paginado). */
export interface AuditoriaCambiosPaginadoFiltros {
  /** Una categoría puntual (gana sobre `categorias`). */
  categoria?: string;
  /** CSV con las categorías del scope del tab (cuando no hay puntual). */
  categorias?: string;
  accion?: string;
  texto?: string;
  page?: number;
  pageSize?: number;
}

/** Filtros del listado PAGINADO de documentos (GET /documentos/auditoria). */
export interface AuditoriaDocumentosPaginadoFiltros {
  accion?: string;
  categoria?: string;
  numeroSolicitud?: string;
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

function buildQueryString(
  filtros: Record<string, string | number | undefined | null>
): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/** Listado PAGINADO de cambios · filtros server-side (endpoint nuevo /paginado). */
export async function listarAuditoriaCambios(
  filtros: AuditoriaCambiosPaginadoFiltros = {}
): Promise<Paginado<AdminAuditEntry>> {
  const url = `/api/admin/auditoria-cambios/paginado${buildQueryString({ ...filtros })}`;
  const { data } = await axiosClient.get<Paginado<AdminAuditEntry>>(url);
  return data;
}

/** Listado PAGINADO de documentos · filtros server-side (reemplazó al mecanismo `top`). */
export async function listarAuditoriaDocumentos(
  filtros: AuditoriaDocumentosPaginadoFiltros = {}
): Promise<Paginado<DocumentoAuditEntry>> {
  const url = `/api/admin/documentos/auditoria${buildQueryString({ ...filtros })}`;
  const { data } = await axiosClient.get<Paginado<DocumentoAuditEntry>>(url);
  return data;
}

/**
 * Descarga un CSV via blob · necesario porque el endpoint devuelve
 * `text/csv` directo y queremos disparar el download del browser. El
 * filename real viene en `Content-Disposition`, con fallback local.
 */
async function descargarCsv(url: string, fallbackFilename: string): Promise<void> {
  const response = await axiosClient.get<Blob>(url, { responseType: "blob" });
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export async function descargarAuditoriaCambiosCsv(
  filtros: AuditoriaFiltros = {}
): Promise<void> {
  const url = `/api/admin/auditoria-cambios/csv${buildQueryString({ ...filtros })}`;
  const ts = new Date().toISOString().slice(0, 19);
  await descargarCsv(url, `auditoria-${filtros.categoria ?? "todas"}-${ts}.csv`);
}

export async function descargarAuditoriaDocumentosCsv(
  filtros: AuditoriaDocumentosFiltros = {}
): Promise<void> {
  const url = `/api/admin/documentos/auditoria/csv${buildQueryString({ ...filtros })}`;
  const ts = new Date().toISOString().slice(0, 19);
  await descargarCsv(url, `auditoria-documentos-${ts}.csv`);
}
